import { useMemo, useState, useEffect } from 'react';
import DishCost from './DishCost';

export default function PlatsMenuTab({ menuItems, ingredients, units, dishCategories = [], onSaveMenuItem, onDeleteMenuItem, onImportSuccess, getPortionsRemaining }) {
    const [form, setForm] = useState({
        id: null,
        name: '',
        number: '',
        dish_category_id: null,
        price: 0,
        recipe: [],
        purchasePrice: 0,
        supplier: '',
        purchaseDate: '',
        perishable: false,
        expirationDate: '',
        material_cost: 0,
        labor_charges: 0,
    });
    const [selectedId, setSelectedId] = useState(null);
    const [visibleCostId, setVisibleCostId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [dishImportFile, setDishImportFile] = useState(null);
    const [dishImportLoading, setDishImportLoading] = useState(false);
    const [dishImportMessage, setDishImportMessage] = useState('');
    const [dishCostsCache, setDishCostsCache] = useState({}); // Cache pour les coûts FIFO
    const [dishCostDetailsCache, setDishCostDetailsCache] = useState({}); // Détails de coût par ingrédient
    const [costCalculationMode, setCostCalculationMode] = useState(null); // null | 'from-ingredients' | 'from-form'
    const [calculatedMaterialCost, setCalculatedMaterialCost] = useState(null);
    const [calculatedCostDetails, setCalculatedCostDetails] = useState([]);
    const [showFormModal, setShowFormModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [recipeEntryType, setRecipeEntryType] = useState('ingredient');
    const [recipeEntryId, setRecipeEntryId] = useState('');
    const [recipeEntryQty, setRecipeEntryQty] = useState(1);
    const [recipeEntryUnitId, setRecipeEntryUnitId] = useState('');
    const [recipeEntryNotes, setRecipeEntryNotes] = useState('');
    const [recipeEntryUseInverse, setRecipeEntryUseInverse] = useState(false);
    const [recipeEntryTotalQty, setRecipeEntryTotalQty] = useState('');
    const [recipeEntryServings, setRecipeEntryServings] = useState('');

    const selectedDish = useMemo(
        () => menuItems.find((item) => item.id === selectedId) ?? null,
        [menuItems, selectedId]
    );

    const isRecipeIngredientOption = (item) => {
        if (!item || typeof item !== 'object') return false;
        const itemId = Number(item.id ?? 0);
        if (itemId && (menuItems || []).some((menuItem) => Number(menuItem.id) === itemId)) {
            return false;
        }

        return !Boolean(
                item?.type === 'dish' ||
                item?.is_dish === true ||
                item?.category === 'plats' ||
                item?.category === 'plat'
            );
    };

    const recipeIngredients = useMemo(() => {
        const source = Array.isArray(ingredients) ? ingredients : [];
        return source.filter(isRecipeIngredientOption);
    }, [ingredients, menuItems]);

    const componentOptions = useMemo(() => {
        // Components removed — keep array empty for compatibility
        return [];
    }, [menuItems]);

    const recipeOptions = useMemo(() => {
        // Only ingredients are valid recipe options now
        return [...recipeIngredients];
    }, [recipeIngredients, componentOptions]);

    const ingredientMap = useMemo(() => {
        const map = new Map();
        (recipeIngredients || []).forEach((ingredient) => map.set(ingredient.id, ingredient));
        return map;
    }, [recipeIngredients]);

    const componentMap = useMemo(() => {
        return new Map();
    }, [componentOptions]);

    const recipeOptionMap = useMemo(() => {
        const map = new Map();
        (recipeOptions || []).forEach((item) => {
            map.set(`ingredient:${item.id}`, item);
            map.set(`${item.id}`, item);
            if (item.type === 'component') {
                map.set(`component:${item.id}`, item);
            }
        });
        return map;
    }, [recipeOptions]);

    const unitMap = useMemo(() => {
        const map = new Map();
        (units || []).forEach((unit) => map.set(unit.id, unit));
        return map;
    }, [units]);

    const dishCategoryMap = useMemo(() => {
        const map = new Map();
        (dishCategories || []).forEach((category) => map.set(Number(category.id), category));
        return map;
    }, [dishCategories]);

    const getDefaultUnitId = (ingredientId, type = 'ingredient') => {
        const ingredient = ingredientMap.get(Number(ingredientId));
        return Number(ingredient?.unit_id || 0) || null;
    };

    const getRecipeOptionsForType = (type) => {
        return recipeIngredients;
    };

    useEffect(() => {
        const options = getRecipeOptionsForType(recipeEntryType);
        if (!options.length) {
            setRecipeEntryId('');
            return;
        }

        const currentId = Number(recipeEntryId || 0);
        if (!currentId || !options.some((option) => Number(option.id) === currentId)) {
            const nextId = options[0]?.id ?? 0;
            setRecipeEntryId(String(nextId));
            setRecipeEntryUnitId(getDefaultUnitId(nextId, recipeEntryType === 'component' ? 'component' : 'ingredient'));
        }
    }, [componentOptions, recipeIngredients, recipeEntryId, recipeEntryType]);

    const handleRecipeEntryTypeChange = (type) => {
        setRecipeEntryType(type);
        const options = getRecipeOptionsForType(type);
        const nextId = options[0]?.id ?? 0;
        setRecipeEntryId(String(nextId));
        setRecipeEntryQty(1);
        setRecipeEntryUnitId(getDefaultUnitId(nextId, type === 'component' ? 'component' : 'ingredient'));
        setRecipeEntryNotes('');
        setRecipeEntryUseInverse(false);
        setRecipeEntryTotalQty('');
        setRecipeEntryServings('');
    };

    const formatMoney = (value) => Number(value || 0).toFixed(2).replace('.', ',');

    const normalizeRecipeLine = (line) => {
        const inferredType = line.type
            ? line.type
            : (line.source_type === 'component' || line.component_id || line.componentId ? 'component' : line.source_type === 'decoration' ? 'decoration' : 'ingredient');
        const normalizedType = inferredType === 'decoration' ? 'decoration' : inferredType;

        const componentId = Number(line.component?.id ?? line.component_id ?? line.componentId ?? line.component ?? line.ingredientId ?? line.ingredient_id ?? line.id ?? 0);
        const ingredientId = Number(line.ingredient?.id ?? line.ingredient_id ?? line.ingredientId ?? line.id ?? 0);
        const itemId = normalizedType === 'component' ? componentId : ingredientId;

        return {
            ingredientId: normalizedType === 'component' ? null : itemId,
            componentId: normalizedType === 'component' ? itemId : null,
            type: normalizedType,
            qty: Number(line.qty ?? line.quantity ?? 0),
            unitId: Number(line.unitId ?? line.unit_id ?? 0) || getDefaultUnitId(itemId, normalizedType),
            notes: line.notes || '',
        };
    };

    const getRecipeLineItemId = (line) => {
        const inferredType = line.type || line.source_type || (line.component_id || line.componentId ? 'component' : 'ingredient');
        if (inferredType === 'component') {
            return Number(line.componentId ?? line.component_id ?? line.component?.id ?? line.component ?? line.ingredientId ?? line.ingredient_id ?? line.id ?? 0);
        }
        return Number(line.ingredientId ?? line.ingredient_id ?? line.ingredient?.id ?? line.ingredient ?? line.id ?? 0);
    };

    // If ingredients list changes (e.g. a new ingredient was added elsewhere),
    // ensure existing recipe lines reference valid ingredient IDs and default units.
    useEffect(() => {
        if (!recipeOptions || recipeOptions.length === 0) return;
        setForm((prev) => {
            if (!prev || !Array.isArray(prev.recipe) || prev.recipe.length === 0) return prev;
            let changed = false;
            const nextRecipe = prev.recipe.map((line) => {
                const lineType = line.type || 'ingredient';
                const lineId = getRecipeLineItemId(line);
                const lineKey = `${lineType}:${lineId}`;

                if (!recipeOptionMap.has(lineKey)) {
                    const matchingOption = recipeOptions.find((option) => option.id === lineId && option.type === lineType);
                    if (!matchingOption) {
                        return line;
                    }
                }

                if ((line.type || '').toString() === 'component' && !line.componentId) {
                    changed = true;
                    return { ...line, componentId: lineId, ingredientId: null };
                }

                return line;
            });
            return changed ? { ...prev, recipe: nextRecipe } : prev;
        });
    }, [recipeOptions, recipeOptionMap]);

    useEffect(() => {
        if (!recipeIngredients || recipeIngredients.length === 0) return;
        setForm((prev) => {
            if (!prev || !Array.isArray(prev.recipe) || prev.recipe.length === 0) return prev;
            let changed = false;
            const nextRecipe = prev.recipe.map((line) => {
                const ingredientId = getRecipeLineItemId(line);
                const ingredient = ingredientMap.get(ingredientId);
                const defaultUnit = getDefaultUnitId(ingredientId);

                if (ingredient && (line.unitId == null || line.unitId === 0) && defaultUnit) {
                    changed = true;
                    return {
                        ...line,
                        unitId: defaultUnit,
                    };
                }
                return line;
            });
            return changed ? { ...prev, recipe: nextRecipe } : prev;
        });
    }, [recipeIngredients, ingredientMap, componentMap]);

    const dishCostSignature = useMemo(() => {
        return (menuItems || [])
            .map((dish) => {
                const recipe = Array.isArray(dish?.recipe) ? dish.recipe : [];
                const recipeKey = recipe
                    .map((line) => `${getRecipeLineItemId(line)}:${Number(line.qty ?? line.quantity ?? 0)}:${line.type || 'ingredient'}`)
                    .join('|');
                return `${dish?.id ?? 'new'}:${recipeKey}`;
            })
            .join('||');
    }, [menuItems]);

    // Calculer les coûts FIFO pour tous les plats
    useEffect(() => {
        const calculateAllDishCosts = async () => {
            const newCostsCache = {};
            const newCostDetailsCache = {};

            for (const dish of (menuItems || [])) {
                if (!dish?.recipe?.length) {
                    newCostsCache[dish.id] = 0;
                    newCostDetailsCache[dish.id] = [];
                    continue;
                }

                try {
                    const recipePayload = (dish.recipe || [])
                        .map((line) => {
                            const lineType = (line.type || '').toString();
                            const normalizedType = lineType === 'decoration' ? 'decoration' : lineType;
                            const componentId = Number(line.componentId ?? line.component_id ?? line.component ?? line.ingredientId ?? line.ingredient_id ?? 0);
                            const ingredientId = Number(line.ingredientId ?? line.ingredient_id ?? line.id ?? 0);

                            return {
                                type: normalizedType,
                                ingredientId: normalizedType === 'component' ? null : ingredientId,
                                componentId: normalizedType === 'component' ? componentId : null,
                                qty: Number(line.qty ?? line.quantity ?? 0),
                            };
                        })
                        .filter((line) => line.qty > 0 && ((line.type === 'component' && line.componentId > 0) || (line.type === 'ingredient' && line.ingredientId > 0) || line.type === 'decoration'));

                    if (!recipePayload.length) {
                        newCostsCache[dish.id] = 0;
                        newCostDetailsCache[dish.id] = [];
                        continue;
                    }

                    const response = await fetch('/api/inventory/calculate-recipe-cost', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                        },
                        body: JSON.stringify({ recipe: recipePayload }),
                    });

                    if (!response.ok) {
                        console.warn(`API cost calc failed for dish ${dish.id} (HTTP ${response.status})`);
                        newCostsCache[dish.id] = calculateFallbackCost(dish);
                        newCostDetailsCache[dish.id] = [];
                        continue;
                    }

                    // Safely parse JSON response
                    let data = null;
                    try {
                        const text = await response.text();
                        const contentType = response.headers.get('content-type') || '';
                        const looksLikeJson = contentType.includes('application/json') || text.trim().startsWith('{') || text.trim().startsWith('[');

                        if (text && text.trim() && looksLikeJson) {
                            data = JSON.parse(text);
                        }
                    } catch (parseErr) {
                        console.warn(`Failed to parse API response for dish ${dish.id}:`, parseErr);
                        newCostsCache[dish.id] = calculateFallbackCost(dish);
                        newCostDetailsCache[dish.id] = [];
                        continue;
                    }

                    if (data && typeof data === 'object') {
                        let cost = Number(data.total_cost ?? 0) || 0;
                        const plateCount = Number(dish.number) || 1;
                        if (plateCount > 1) {
                            cost = cost / plateCount;
                        }
                        newCostsCache[dish.id] = Math.max(0, cost);
                        newCostDetailsCache[dish.id] = Array.isArray(data.recipe_details) ? data.recipe_details : [];
                    } else {
                        newCostsCache[dish.id] = calculateFallbackCost(dish);
                        newCostDetailsCache[dish.id] = [];
                    }
                } catch (error) {
                    console.error(`Error calculating FIFO cost for dish ${dish.id}:`, error);
                    newCostsCache[dish.id] = calculateFallbackCost(dish);
                    newCostDetailsCache[dish.id] = [];
                }
            }

            setDishCostsCache(newCostsCache);
            setDishCostDetailsCache(newCostDetailsCache);
        };

        if ((menuItems || []).length > 0) {
            calculateAllDishCosts();
        }
    }, [dishCostSignature, ingredientMap, componentMap]);

    // Fallback: calcul simple si l'API échoue
    const calculateFallbackCost = (dish) => {
        if (!dish?.recipe?.length) return 0;
        return dish.recipe.reduce((sum, line) => {
            const ingredientId = Number(line.ingredientId ?? line.ingredient_id ?? 0);
            const isComponent = (line.type || '').toString() === 'component';
            const ingredient = (isComponent ? componentMap.get(ingredientId) : ingredientMap.get(ingredientId)) || {};
            // Utiliser unit_price en priorité, fallback sur cost_per_unit
            const unitPrice = parseFloat(ingredient?.unit_price ?? ingredient?.cost_per_unit ?? 0) || 0;
            return sum + unitPrice * Number(line.qty || 0);
        }, 0);
    };

    const getDishCostTotal = (dish) => {
        return dishCostsCache[dish?.id] ?? 0;
    };

    const getDishCostDetails = (dish) => {
        return dishCostDetailsCache[dish?.id] ?? [];
    };

    // Calculer le coût d'un plat basé sur les derniers prix unitaires (dernier batch ou mouvement)
    const getDishPriceFromUnitCosts = (dish) => {
        if (!dish?.recipe?.length) return 0;
        return dish.recipe.reduce((sum, line) => {
            const ingredientId = Number(line.ingredientId ?? line.ingredient_id ?? 0);
            const isComponent = (line.type || '').toString() === 'component';
            const ingredient = (isComponent ? componentMap.get(ingredientId) : ingredientMap.get(ingredientId)) || {};
            const quantity = Number(line.qty ?? line.quantity ?? 0);

            const directUnitPrice = parseFloat(ingredient?.unit_price ?? ingredient?.last_price ?? ingredient?.cost_per_unit ?? ingredient?.purchasePrice ?? 0) || 0;
            const batch = ingredient?.batch || ingredient?.last_batch || {};
            const batchTotal = Number(batch?.purchase_price ?? batch?.unit_cost ?? 0);
            const batchQty = Number(batch?.quantity ?? batch?.qty ?? 0);
            const unitPrice = batchTotal > 0 && batchQty > 0 ? batchTotal / batchQty : directUnitPrice;

            return sum + (unitPrice * quantity);
        }, 0);
    };

    const calculateMaterialCostFromIngredients = async () => {
        if (!form.recipe || form.recipe.length === 0) {
            alert('Veuillez ajouter au moins un ingrédient à la recette.');
            return;
        }

        try {
            const response = await fetch('/api/inventory/calculate-recipe-cost', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                },
                body: JSON.stringify({
                    recipe: (form.recipe || [])
                        .map((line) => {
                            const normalizedType = (line.type || '').toString() === 'decoration' ? 'decoration' : ((line.type || '').toString() === 'component' ? 'component' : 'ingredient');
                            const ingredientId = Number(line.ingredientId ?? line.ingredient_id ?? line.id ?? 0);
                            const componentId = Number(line.componentId ?? line.component_id ?? line.component ?? 0);
                            return {
                                type: normalizedType,
                                ingredientId: normalizedType === 'component' ? null : ingredientId,
                                componentId: normalizedType === 'component' ? componentId : null,
                                qty: Number(line.qty ?? line.quantity ?? 0),
                            };
                        })
                        .filter((line) => line.qty > 0 && ((line.type === 'component' && line.componentId > 0) || (line.type === 'ingredient' && line.ingredientId > 0) || line.type === 'decoration')),
                }),
            });

            if (!response.ok) {
                alert(`Erreur API: HTTP ${response.status}`);
                return;
            }

            // Safely parse JSON response
            let data = null;
            try {
                const text = await response.text();
                if (text && text.trim()) {
                    data = JSON.parse(text);
                }
            } catch (parseErr) {
                console.warn('Failed to parse API cost response:', parseErr);
                alert('Erreur lors du calcul du coût matière.');
                return;
            }

            if (data && typeof data === 'object') {
                let cost = Number(data.total_cost ?? 0) || 0;
                const plateCount = Number(form.number) || 1;
                if (plateCount > 1) {
                    cost = cost / plateCount;
                }
                const normalizedDetails = Array.isArray(data.recipe_details) ? data.recipe_details : [];
                setCalculatedMaterialCost(Math.max(0, cost));
                setCalculatedCostDetails(normalizedDetails);
                setForm({ ...form, material_cost: Math.max(0, cost) });
                setCostCalculationMode('from-ingredients');
            } else {
                alert('Erreur lors du calcul du coût matière.');
            }
        } catch (error) {
            console.error('Erreur calcul coût matière:', error);
            alert('Erreur lors du calcul du coût matière.');
        }
    };

    const toggleIngredients = (id) => setExpandedId((prev) => (prev === id ? null : id));

    const resetForm = () => {
        setSelectedId(null);
        setForm({
            id: null,
            name: '',
            number: '',
            dish_category_id: null,
            price: 0,
            recipe: [],
            purchasePrice: 0,
            supplier: '',
            purchaseDate: '',
            perishable: false,
            expirationDate: '',
            material_cost: 0,
            labor_charges: 0,
        });
        setCostCalculationMode(null);
        setCalculatedMaterialCost(null);
        setCalculatedCostDetails([]);
        setRecipeEntryType('ingredient');
        setRecipeEntryId('');
        setRecipeEntryQty(1);
        setRecipeEntryUnitId('');
        setRecipeEntryNotes('');
        setRecipeEntryUseInverse(false);
        setRecipeEntryTotalQty('');
        setRecipeEntryServings('');
    };

    const handleEdit = (dish) => {
        setSelectedId(dish.id);
        setForm({
            id: dish.id,
            name: dish.name || '',
            number: dish.number ?? '',
            dish_category_id: dish.dish_category_id ?? dish.dishCategory?.id ?? null,
            price: dish.price ?? 0,
            recipe: (dish.recipe || [])
                .map(normalizeRecipeLine)
                .filter((line) => Number(line.ingredientId ?? 0) > 0),
            purchasePrice: dish.purchasePrice ?? 0,
            supplier: dish.supplier || '',
            purchaseDate: dish.purchaseDate || '',
            perishable: dish.perishable ?? false,
            expirationDate: dish.expirationDate || '',
            material_cost: dish.material_cost ?? 0,
            labor_charges: dish.labor_charges ?? 0,
            
        });
        setCalculatedMaterialCost(null);
        setCostCalculationMode(null);
        setShowFormModal(true);
    };

    const handleImportDishes = async (event) => {
        event.preventDefault();
        if (!dishImportFile) {
            setDishImportMessage('Veuillez sélectionner un fichier CSV.');
            return;
        }

        setDishImportLoading(true);
        setDishImportMessage('');

        const formData = new FormData();
        formData.append('file', dishImportFile);

        try {
            const response = await fetch('/api/import/dishes', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
                body: formData,
            });
            const data = await response.json();
            if (!response.ok) {
                setDishImportMessage(data.message || 'Erreur lors de l’import des plats.');
            } else {
                setDishImportMessage(data.message || 'Import des plats réussi.');
                setDishImportFile(null);
                const input = document.getElementById('dishImportFile');
                if (input) input.value = '';
                onImportSuccess?.();
            }
        } catch (error) {
            console.error('Import plats error:', error);
            setDishImportMessage('Erreur lors de l’import des plats.');
        } finally {
            setDishImportLoading(false);
        }
    };

    const updateRecipeLine = (index, field, value) => {
        const nextRecipe = [...form.recipe];
        let nextRecipeLine = {
            ...nextRecipe[index],
        };

        if (field === 'ingredientId') {
            const [type, idString] = String(value).split(':');
            const itemId = Number(idString || value);
            const normalizedType = type === 'component' ? 'component' : type === 'decoration' ? 'decoration' : 'ingredient';
            nextRecipeLine = {
                ...nextRecipeLine,
                type: normalizedType,
                ingredientId: normalizedType === 'component' ? null : itemId,
                componentId: normalizedType === 'component' ? itemId : null,
                unitId: normalizedType === 'component' ? null : getDefaultUnitId(itemId, normalizedType),
            };
        } else {
            const nextValue = field === 'qty'
                ? Number(value)
                : value === ''
                    ? null
                    : Number(value);

            nextRecipeLine = {
                ...nextRecipeLine,
                [field]: nextValue,
            };
        }

        nextRecipe[index] = nextRecipeLine;
        setForm({ ...form, recipe: nextRecipe });
    };

    const addRecipeLine = () => {
        const options = getRecipeOptionsForType(recipeEntryType);
        const defaultId = Number(recipeEntryId || options[0]?.id || 0);

        if (!defaultId) {
            alert('Veuillez sélectionner un élément à ajouter à la recette.');
            return;
        }

        const usesInverseQty = recipeEntryType === 'decoration' && recipeEntryUseInverse;
        const resolvedQty = usesInverseQty && Number(recipeEntryTotalQty) > 0 && Number(recipeEntryServings) > 0
            ? Number(recipeEntryTotalQty) / Number(recipeEntryServings)
            : Number(recipeEntryQty || 1);

        setForm({
            ...form,
            recipe: [
                ...form.recipe,
                (recipeEntryType === 'decoration' && usesInverseQty)
                    ? {
                        ingredientId: defaultId,
                        type: recipeEntryType,
                        qty: resolvedQty,
                        unitId: recipeEntryUnitId || getDefaultUnitId(defaultId, 'decoration'),
                        notes: recipeEntryNotes || '',
                        totalQty: Number(recipeEntryTotalQty) || null,
                        servings: Number(recipeEntryServings) || null,
                    }
                    : {
                        ingredientId: defaultId,
                        type: recipeEntryType,
                        qty: resolvedQty,
                        unitId: recipeEntryUnitId || getDefaultUnitId(defaultId, 'ingredient'),
                        notes: recipeEntryNotes || '',
                    },
            ],
        });

        setRecipeEntryQty(1);
        setRecipeEntryTotalQty('');
        setRecipeEntryServings('');
        setRecipeEntryNotes('');
        setRecipeEntryUseInverse(false);
        setRecipeEntryUnitId(getDefaultUnitId(defaultId, recipeEntryType === 'component' ? 'component' : 'ingredient'));
    };

    const removeRecipeLine = (index) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette ligne de recette ?')) return;
        const nextRecipe = form.recipe.filter((_, idx) => idx !== index);
        setForm({ ...form, recipe: nextRecipe });
    };

    const getRecipeLineUnitId = (line) => {
        const selectedUnitId = Number(line.unitId ?? line.unit_id ?? 0);
        if (selectedUnitId > 0) return selectedUnitId;
        const ingredientId = getRecipeLineItemId(line);
        const ingredient = ingredientMap.get(ingredientId);
        return Number(ingredient?.unit_id || 0) || null;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (submitting) return;
        if (!form.name.trim()) return;

        const mappedIngredients = (form.recipe || [])
            .map((line) => {
                const unitId = getRecipeLineUnitId(line);
                const type = line.type || 'ingredient';
                const ingredientId = getRecipeLineItemId(line);
                return {
                    type,
                    id: ingredientId,
                    componentId: type === 'component' ? ingredientId : null,
                    quantity: Number(line.qty ?? line.quantity ?? 0),
                    unit_id: unitId || null,
                    notes: line.notes || '',
                };
            })
            .filter((line) => line.id > 0 && line.quantity > 0);

        const payload = {
            id: form.id,
            name: form.name,
            number: form.number ? Number(form.number) : null,
            dish_category_id: form.dish_category_id ? Number(form.dish_category_id) : null,
            price: Number(form.price),
            purchasePrice: Number(form.purchasePrice),
            supplier: form.supplier,
            purchaseDate: form.purchaseDate,
            perishable: form.perishable,
            expirationDate: form.expirationDate,
            material_cost: Number(form.material_cost) || 0,
            labor_charges: Number(form.labor_charges) || 0,
            is_component: Boolean(form.is_component ?? false),
        };

        // N'ajouter la clé de recette que si au moins une ligne valide existe.
        if (mappedIngredients.length > 0) {
            payload.ingredients = mappedIngredients;
            payload.recipe = mappedIngredients;
        }

        try {
            setSubmitting(true);
            console.log('PlatsMenuTab.handleSubmit: form.recipe', form.recipe);
            console.log('PlatsMenuTab.handleSubmit: mappedIngredients', mappedIngredients);
            console.log('PlatsMenuTab.handleSubmit: sending payload', payload);
            await onSaveMenuItem(payload);
            console.log('PlatsMenuTab.handleSubmit: onSaveMenuItem resolved');
            resetForm();
            setShowFormModal(false);
        } catch (error) {
            console.error('Erreur de sauvegarde du plat :', error);
            alert(error?.message || 'Erreur lors de la sauvegarde du plat');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Plats & Menu</p>
                        <h2 className="text-3xl font-semibold text-green-900">Création et gestion des recettes</h2>
                    </div>
                    <p className="max-w-xl text-sm text-green-600">
                        CRUD complet des plats avec gestion des ingrédients et barre de portions restantes.
                    </p>
                </div>
            </section>

            <div className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/10">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Import CSV</p>
                        <h3 className="text-xl font-semibold text-green-900">Importer un fichier de plats</h3>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <input
                            id="dishImportFile"
                            type="file"
                            accept=".csv"
                            onChange={(event) => setDishImportFile(event.target.files?.[0] ?? null)}
                            className="w-full rounded-3xl border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900 focus:border-green-500 focus:outline-none sm:w-auto"
                        />
                        <button
                            type="button"
                            onClick={handleImportDishes}
                            disabled={dishImportLoading}
                            className="rounded-3xl bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-50"
                        >
                            {dishImportLoading ? 'Import en cours...' : 'Importer'}
                        </button>
                    </div>
                </div>
                {dishImportMessage && (
                    <p className={`mt-3 text-sm ${dishImportMessage.startsWith('Erreur') ? 'text-rose-600' : 'text-green-600'}`}>
                        {dishImportMessage}
                    </p>
                )}
                <p className="mt-3 text-sm text-slate-600">
                    Format CSV attendu : Nom du plat, Prix vente, Coût matière (optionnel), Charges/Frais (optionnel), Catégorie (optionnel), Ingrédient 1, Quantité 1, Unité 1, Ingrédient 2, Quantité 2, Unité 2...
                </p>
                <p className="mt-2 text-xs text-slate-500">
                    Exemple d'en-tête : Nom du plat,Prix vente,Coût matière,Charges,Catégorie,Ingrédient 1,Quantité 1,Unité 1,...
                </p>
                <p className="mt-2 text-xs text-rose-600">
                    Remarque : les colonnes calculées comme « COEF MARGE » ou « Marge » ne sont pas importées — elles sont recalculées dans l'application à partir du prix et des coûts.
                </p>
            </div>

            <div className="flex flex-col gap-6">

                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-green-900">Plats & Recettes</h3>
                    <div>
                        <button type="button" onClick={() => { resetForm(); setShowFormModal(true); }} className="rounded-3xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-500">Nouveau plat</button>
                    </div>
                </div>

                {showFormModal && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center">
                        <div className="absolute inset-0 bg-black/50" onClick={() => setShowFormModal(false)} />
                                <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-lg z-50 flex flex-col max-h-[90vh]">
                                    <div className="p-6 overflow-y-auto flex-1">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-xl font-semibold text-green-900">{selectedDish ? 'Modifier un plat' : 'Nouveau plat'}</h3>
                                            <button type="button" onClick={() => setShowFormModal(false)} className="text-slate-600">✕</button>
                                        </div>
                                        <form className="space-y-4" onSubmit={handleSubmit}>
                                            <div>
                                                <label className="text-sm text-green-600">Nom du plat</label>
                                                <input
                                                    value={form.name}
                                                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                    placeholder="Ex. Club burger"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm text-green-600">Nombre de plats</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={form.number}
                                                    onChange={(event) => setForm({ ...form, number: event.target.value })}
                                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                    placeholder="Ex. 12"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm text-green-600">Catégorie de plat</label>
                                                <select
                                                    value={form.dish_category_id ?? ''}
                                                    onChange={(event) => setForm({ ...form, dish_category_id: event.target.value ? Number(event.target.value) : null })}
                                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                >
                                                    <option value="">Sélectionner une catégorie</option>
                                                    {dishCategories.map((category) => (
                                                        <option key={category.id} value={category.id}>
                                                            {category.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div>
                                                    <label className="text-sm text-green-600">Prix vente (Ar)</label>
                                                    <input
                                                        type="number"
                                                        step="0.5"
                                                        min="0"
                                                        value={form.price}
                                                        onChange={(event) => setForm({ ...form, price: event.target.value })}
                                                        className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm text-green-600">Coût matière (Ar)</label>
                                                    <input
                                                        type="number"
                                                        step="0.5"
                                                        min="0"
                                                        value={form.material_cost}
                                                        onChange={(event) => setForm({ ...form, material_cost: event.target.value })}
                                                        className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                        placeholder="Coût des ingrédients"
                                                    />
                                                    {costCalculationMode === 'from-ingredients' && calculatedMaterialCost !== null && (
                                                        <p className="mt-1 text-xs text-green-600">Calculé automatiquement</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="text-sm text-green-600">Charges/Frais (Ar)</label>
                                                    <input
                                                        type="number"
                                                        step="0.5"
                                                        min="0"
                                                        value={form.labor_charges}
                                                        onChange={(event) => setForm({ ...form, labor_charges: event.target.value })}
                                                        className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                        placeholder="Main-d'œuvre, frais..."
                                                    />
                                                </div>
                                            </div>
                                            <div className="rounded-3xl border border-green-400 bg-green-50/90 p-4">
                                                <p className="text-sm font-semibold text-green-900 mb-3">Type de plat</p>
                                                <p className="text-xs text-slate-600">Les composants ont été retirés ; seul les plats standards sont supportés.</p>
                                            </div>
                                            <div className="rounded-3xl border border-blue-300 bg-blue-50/90 p-4">
                                                <p className="text-sm font-semibold text-blue-900 mb-2">Coût de revient estimé: <span className="text-lg text-blue-600">Ar {formatMoney(Number(form.material_cost || 0) + Number(form.labor_charges || 0))}</span></p>
                                                <p className="text-xs text-slate-600 mb-3">Coût selon derniers prix: <span className="text-sm text-blue-600">Ar {formatMoney(getDishPriceFromUnitCosts(form))}</span></p>
                                                <div className="flex flex-col gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={calculateMaterialCostFromIngredients}
                                                        className="rounded-3xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500"
                                                    >
                                                        📊 Calculer coût matière à partir des ingrédients
                                                    </button>
                                                    <p className="text-xs text-slate-600">Le coût matière sera calculé automatiquement en fonction de la recette.</p>
                                                </div>
                                                {costCalculationMode === 'from-ingredients' && calculatedCostDetails.length > 0 && (
                                                    <div className="mt-4 rounded-3xl border border-blue-200 bg-blue-50 p-4 text-xs text-slate-700">
                                                        <p className="font-semibold text-blue-800 mb-2">Détail du calcul</p>
                                                        <div className="space-y-3">
                                                            {calculatedCostDetails.map((detail, index) => {
                                                                const path = Array.isArray(detail.componentPath) && detail.componentPath.length > 0
                                                                    ? detail.componentPath.join(' → ')
                                                                    : 'Direct';
                                                                const unitCost = detail.quantity > 0 ? Number(detail.cost ?? 0) / Number(detail.quantity || 1) : 0;

                                                                return (
                                                                    <div key={`detail-${index}`} className="rounded-2xl bg-white p-3 shadow-sm">
                                                                        <div className="flex items-center justify-between gap-2">
                                                                            <div className="font-medium text-blue-700">{detail.ingredientName || detail.ingredient?.name || `Ingrédient ${detail.ingredientId ?? detail.ingredient_id}`}</div>
                                                                            <div className="font-semibold text-blue-900">Ar {formatMoney(detail.cost ?? 0)}</div>
                                                                        </div>
                                                                        <div className="mt-1 text-slate-500">
                                                                            {detail.quantity} {detail.unit || ''} × Ar {formatMoney(unitCost)}
                                                                        </div>
                                                                        <div className="mt-1 text-xs text-slate-500">Type: {detail.componentPath && detail.componentPath.length ? 'Composant' : 'Ingrédient direct'}</div>
                                                                        <div className="text-xs text-slate-500">Chemin: {path}</div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="rounded-3xl border border-green-400 bg-green-50/90 p-4">
                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                    <p className="text-sm font-semibold text-green-900">Recette</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        <button type="button" onClick={() => handleRecipeEntryTypeChange('ingredient')} className={`rounded-3xl px-3 py-2 text-sm font-semibold ${recipeEntryType === 'ingredient' ? 'bg-green-600 text-white' : 'border border-green-300 bg-white text-green-700'}`}>
                                                            Ingrédient
                                                        </button>
                                                        <button type="button" onClick={() => handleRecipeEntryTypeChange('decoration')} className={`rounded-3xl px-3 py-2 text-sm font-semibold ${recipeEntryType === 'decoration' ? 'bg-green-600 text-white' : 'border border-green-300 bg-white text-green-700'}`}>
                                                            Décoration
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="mt-4 rounded-3xl border border-green-300 bg-white p-4">
                                                    <div className="grid gap-3 md:grid-cols-[1.2fr_0.7fr_0.7fr]">
                                                        <div>
                                                            <label className="text-sm text-green-600">Élément</label>
                                                            <select
                                                                value={recipeEntryId}
                                                                onChange={(event) => {
                                                                    const nextId = event.target.value;
                                                                    setRecipeEntryId(nextId);
                                                                    setRecipeEntryUnitId(getDefaultUnitId(nextId, recipeEntryType === 'component' ? 'component' : 'ingredient'));
                                                                }}
                                                                className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                            >
                                                                {(() => {
                                                                    const opts = getRecipeOptionsForType(recipeEntryType) || [];
                                                                    return opts.length > 0 ? opts.map((item) => (
                                                                        <option key={`${item.type || 'ingredient'}:${item.id}`} value={String(item.id)}>
                                                                            {item.name}{item.type === 'component' ? ' (composant)' : ''}{(item.marque || item.caracteristique) ? ` (${[item.marque, item.caracteristique].filter(Boolean).join(' ')})` : ''}
                                                                        </option>
                                                                    )) : (
                                                                        <option disabled>Aucun élément disponible</option>
                                                                    );
                                                                })()}
                                                            
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="text-sm text-green-600">{recipeEntryType === 'component' ? 'Nombre de portions' : 'Quantité'}</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                value={recipeEntryQty}
                                                                onChange={(event) => setRecipeEntryQty(event.target.value)}
                                                                className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                            />
                                                            {recipeEntryType === 'component' && (
                                                                <p className="mt-2 text-xs text-amber-700">
                                                                    Cette valeur sera utilisée comme nombre de portions du composant dans la recette.
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label className="text-sm text-green-600">Unité</label>
                                                            <select
                                                                value={recipeEntryUnitId ?? ''}
                                                                onChange={(event) => setRecipeEntryUnitId(event.target.value)}
                                                                className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                            >
                                                                <option value="">Unité...</option>
                                                                {units.map((unitItem) => (
                                                                    <option key={unitItem.id} value={unitItem.id}>
                                                                        {unitItem.abbreviation || unitItem.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                                        {recipeEntryType === 'decoration' && (
                                                            <label className="flex items-center gap-2 text-sm text-green-700">
                                                                <input type="checkbox" checked={recipeEntryUseInverse} onChange={() => setRecipeEntryUseInverse((prev) => !prev)} />
                                                                Quantité inverse (quantité totale / nombre de portions)
                                                            </label>
                                                        )}
                                                        <input
                                                            type="text"
                                                            value={recipeEntryNotes}
                                                            onChange={(event) => setRecipeEntryNotes(event.target.value)}
                                                            placeholder="Notes optionnelles"
                                                            className="rounded-3xl border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-900/10"
                                                        />
                                                    </div>
                                                    {recipeEntryType === 'decoration' && recipeEntryUseInverse && (
                                                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                                                            <div>
                                                                <label className="text-sm text-green-600">Quantité totale</label>
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    min="0"
                                                                    value={recipeEntryTotalQty}
                                                                    onChange={(event) => setRecipeEntryTotalQty(event.target.value)}
                                                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-sm text-green-600">Nombre de portions</label>
                                                                <input
                                                                    type="number"
                                                                    step="1"
                                                                    min="1"
                                                                    value={recipeEntryServings}
                                                                    onChange={(event) => setRecipeEntryServings(event.target.value)}
                                                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                                                        <p className="text-xs text-slate-500">Exemple : 100 g pour 50 plats = 2 g par plat.</p>
                                                        <button
                                                            type="button"
                                                            onClick={addRecipeLine}
                                                            className="rounded-3xl bg-green-600 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-green-500"
                                                        >
                                                            Ajouter à la recette
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="mt-4 space-y-3">
                                                    {!form.recipe.length ? (
                                                        <div className="rounded-3xl border border-green-400 bg-white p-4 text-green-700">
                                                            <p className="text-sm text-green-600">
                                                                Aucun ingrédient défini — ce plat peut être fourni par un fournisseur externe.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {form.recipe.filter((line) => (line.type || '').toString() === 'component').length > 0 && (
                                                                <div className="mb-4 rounded-3xl border border-amber-200 bg-amber-50 p-4">
                                                                    <p className="text-sm font-semibold text-amber-800 mb-3">Composants</p>
                                                                    <div className="space-y-4">
                                                                        {form.recipe.filter((line) => (line.type || '').toString() === 'component').map((line, index) => {
                                                                            const selectedId = getRecipeLineItemId(line);
                                                                            const ingredient = componentMap.get(selectedId) || {};
                                                                            const unit = unitMap.get(Number(line.unitId ?? line.unit_id ?? ingredient?.unit_id ?? 0)) || {};
                                                                            const ingredientUnit = unit.abbreviation || ingredient?.unit || 'unité';
                                                                            const unitValue = ingredient?.unit_id ? ingredient.unit_id : (line.unitId ?? '');
                                                                            const unitDisabled = Boolean(ingredient?.unit_id);
                                                                            const lineSelectValue = `${line.type || 'ingredient'}:${selectedId}`;
                                                                            const lineSelectOptions = [...componentOptions];
                                                                            if (selectedId > 0 && !lineSelectOptions.some((item) => `${item.type || 'ingredient'}:${item.id}` === lineSelectValue)) {
                                                                                const currentItem = componentMap.get(selectedId);
                                                                                if (currentItem) {
                                                                                    lineSelectOptions.push(currentItem);
                                                                                }
                                                                            }
                                                                            return (
                                                                                <div key={`component:${selectedId}:${index}`} className="grid gap-3 grid-cols-1 sm:grid-cols-[1.1fr_0.7fr_0.7fr_0.5fr]">
                                                                                    <div className="space-y-2">
                                                                                        <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold bg-amber-100 text-amber-700">Composant</span>
                                                                                        <select
                                                                                            value={lineSelectValue}
                                                                                            onChange={(event) => updateRecipeLine(index, 'ingredientId', event.target.value)}
                                                                                            className="w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                                                        >
                                                                                            {lineSelectOptions.map((item) => (
                                                                                                <option key={`${item.type || 'ingredient'}:${item.id}`} value={`${item.type || 'ingredient'}:${item.id}`}>
                                                                                                    {item.name}{item.type === 'component' ? ' (composant)' : ''}{(item.marque || item.caracteristique) ? ` (${[item.marque, item.caracteristique].filter(Boolean).join(' ')})` : ''}
                                                                                                </option>
                                                                                            ))}
                                                                                            {lineSelectOptions.length === 0 && (
                                                                                                <option disabled>Aucun composant disponible</option>
                                                                                            )}
                                                                                        </select>
                                                                                    </div>
                                                                                    <div>
                                                                                        <input
                                                                                            type="number"
                                                                                            step="0.1"
                                                                                            min="0"
                                                                                            value={line.qty}
                                                                                            onChange={(event) => updateRecipeLine(index, 'qty', event.target.value)}
                                                                                            className="w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                                                            placeholder="Quantité"
                                                                                        />
                                                                                        <p className="mt-2 text-xs text-slate-500">{ingredientUnit}</p>
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="text-xs text-amber-700">Pas d'unité pour les composants</div>
                                                                                    </div>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => removeRecipeLine(index)}
                                                                                        className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-rose-500 bg-red-100 text-rose-500 hover:bg-rose-500/20 text-lg font-light"
                                                                                        title="Supprimer cette ligne"
                                                                                    >
                                                                                        ×
                                                                                    </button>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {form.recipe.filter((line) => (line.type || '').toString() !== 'component').length > 0 && (
                                                                <div className="rounded-3xl border border-green-400 bg-white p-4">
                                                                    <p className="text-sm font-semibold text-green-900 mb-3">Ingrédients et décorations</p>
                                                                    <div className="space-y-4">
                                                                        {form.recipe.filter((line) => (line.type || '').toString() !== 'component').map((line, index) => {
                                                                            const isComponent = (line.type || '').toString() === 'component';
                                                                            const selectedId = getRecipeLineItemId(line);
                                                                            const ingredient = isComponent
                                                                                ? (componentMap.get(selectedId) || {})
                                                                                : (ingredientMap.get(selectedId) || {});
                                                                            const unit = unitMap.get(Number(line.unitId ?? line.unit_id ?? ingredient?.unit_id ?? 0)) || {};
                                                                            const ingredientUnit = unit.abbreviation || ingredient?.unit || 'unité';
                                                                            const unitValue = ingredient?.unit_id ? ingredient.unit_id : (line.unitId ?? '');
                                                                            const unitDisabled = Boolean(ingredient?.unit_id);
                                                                            const lineSelectValue = `${line.type || 'ingredient'}:${selectedId}`;
                                                                            const allowedOptions = isComponent ? componentOptions : recipeIngredients;
                                                                            const lineSelectOptions = [...allowedOptions];
                                                                            if (selectedId > 0 && !lineSelectOptions.some((item) => `${item.type || 'ingredient'}:${item.id}` === lineSelectValue)) {
                                                                                const currentItem = isComponent ? componentMap.get(selectedId) : ingredientMap.get(selectedId);
                                                                                if (currentItem) {
                                                                                    lineSelectOptions.push(currentItem);
                                                                                }
                                                                            }
                                                                            return (
                                                                                <div key={`${line.type || 'ingredient'}:${selectedId}:${index}`} className="grid gap-3 grid-cols-1 sm:grid-cols-[1.1fr_0.7fr_0.7fr_0.5fr]">
                                                                                    <div className="space-y-2">
                                                                                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${line.type === 'decoration' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                                                            {line.type === 'decoration' ? 'Décoration' : 'Ingrédient'}
                                                                                        </span>
                                                                                        <select
                                                                                            value={lineSelectValue}
                                                                                            onChange={(event) => updateRecipeLine(index, 'ingredientId', event.target.value)}
                                                                                            className="w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                                                        >
                                                                                            {lineSelectOptions.map((item) => (
                                                                                                <option key={`${item.type || 'ingredient'}:${item.id}`} value={`${item.type || 'ingredient'}:${item.id}`}>
                                                                                                    {item.name}{item.type === 'component' ? ' (composant)' : item.type === 'decoration' ? ' (décoration)' : ''}{(item.marque || item.caracteristique) ? ` (${[item.marque, item.caracteristique].filter(Boolean).join(' ')})` : ''}
                                                                                                </option>
                                                                                            ))}
                                                                                            {lineSelectOptions.length === 0 && (
                                                                                                <option disabled>Aucun élément disponible</option>
                                                                                            )}
                                                                                        </select>
                                                                                    </div>
                                                                                    <div>
                                                                                        {line.type === 'decoration' && line.totalQty && line.servings ? (
                                                                                            <div className="rounded-3xl border border-purple-100 bg-purple-50 p-3">
                                                                                                <div className="text-sm font-medium text-purple-800">{line.totalQty} {ingredientUnit} pour {line.servings} plats</div>
                                                                                                <div className="text-xs text-purple-600">soit {Number(line.qty || 0)} {ingredientUnit} par plat</div>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    step="0.1"
                                                                                                    min="0"
                                                                                                    value={line.qty}
                                                                                                    onChange={(event) => updateRecipeLine(index, 'qty', event.target.value)}
                                                                                                    className="w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                                                                    placeholder="Quantité"
                                                                                                />
                                                                                                <p className="mt-2 text-xs text-slate-500">{ingredientUnit}</p>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                    <div>
                                                                                        {line.type === 'component' ? (
                                                                                            <div className="text-xs text-amber-700">Pas d'unité pour les composants</div>
                                                                                        ) : (
                                                                                            <select
                                                                                                value={unitValue}
                                                                                                onChange={(event) => updateRecipeLine(index, 'unitId', event.target.value)}
                                                                                                disabled={unitDisabled}
                                                                                                className={`w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none ${unitDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                                                                                                <option value="">Unité...</option>
                                                                                                {units.map((unitItem) => (
                                                                                                    <option key={unitItem.id} value={unitItem.id}>
                                                                                                        {unitItem.abbreviation || unitItem.name}
                                                                                                    </option>
                                                                                                ))}
                                                                                            </select>
                                                                                        )}
                                                                                    </div>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => removeRecipeLine(index)}
                                                                                        className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-rose-500 bg-red-100 text-rose-500 hover:bg-rose-500/20 text-lg font-light"
                                                                                        title="Supprimer cette ligne"
                                                                                    >
                                                                                        ×
                                                                                    </button>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-3">
                                                <button
                                                    type="submit"
                                                    disabled={submitting}
                                                    className={`rounded-3xl bg-green-600 px-5 py-3 font-semibold text-slate-950 hover:bg-green-500 ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    Enregistrer
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { resetForm(); setShowFormModal(false); }}
                                                    className="rounded-3xl border border-green-300 bg-green-50 px-5 py-3 text-green-800 hover:border-green-500"
                                                >
                                                    Annuler
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                 </div>   
                    </div>
                )}

                <div className="max-h-[calc(100vh-220px)] overflow-y-auto rounded-3xl border border-green-400 bg-white shadow-xl shadow-slate-950/20">
                    <table className="min-w-full border-separate border-spacing-0 text-sm">
                        <thead>
                            <tr className="bg-green-50 text-left text-green-800">
                                <th className="border-b border-green-100 px-4 py-4">Plat</th>
                                <th className="border-b border-green-100 px-4 py-4 hidden sm:table-cell">Portions restantes</th>
                                <th className="border-b border-green-100 px-4 py-4">Coût matière</th>
                                <th className="border-b border-green-100 px-4 py-4">Charges</th>
                                <th className="border-b border-green-100 px-4 py-4">Coût total</th>
                                <th className="border-b border-green-100 px-4 py-4">Prix vente</th>
                                <th className="border-b border-green-100 px-4 py-4">Marge</th>
                                <th className="border-b border-green-100 px-4 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {menuItems.map((menuItem) => (
                                <tr key={menuItem.id} className="border-b border-green-100 hover:bg-green-50/70">
                                    <td className="px-4 py-4">
                                        {menuItem.dish_category_id && (
                                            <div className="mb-1 text-xs font-semibold text-green-600 uppercase tracking-[0.1em]">
                                                {dishCategoryMap.get(Number(menuItem.dish_category_id))?.name || 'Catégorie inconnue'}
                                            </div>
                                        )}
                                        <div className="font-semibold text-green-900">{menuItem.name}</div>
                                        {menuItem.number != null && (
                                            <div className="mt-1 text-xs text-green-600">Nombre de plats : {menuItem.number}</div>
                                        )}
                                        <div className="mt-1 text-xs text-green-600">
                                            {(menuItem.recipe || []).length ? `${(menuItem.recipe || []).length} ingrédient(s)` : 'Aucune recette définie'}
                                        </div>
                                        {expandedId === menuItem.id && (
                                            <div className="mt-3 text-sm text-slate-700">
                                                <ul className="list-disc list-inside space-y-1">
                                                    {(menuItem.recipe || []).map((line, idx) => {
                                                        const ingId = getRecipeLineItemId(line);
                                                        let ing = {};
                                                        if ((line.type || '').toString() === 'component') {
                                                            ing = componentMap.get(ingId) || recipeOptionMap.get(`component:${ingId}`) || (menuItems || []).find((m) => Number(m.id) === ingId) || {};
                                                        } else {
                                                            ing = ingredientMap.get(ingId) || {};
                                                        }
                                                        const unit = unitMap.get(Number(line.unitId ?? line.unit_id ?? ing?.unit_id ?? 0)) || {};
                                                        const stockQty = Number((ing?.stock ?? ing?.stock_quantity) || 0);
                                                        const displayName = ing?.name ? ing.name + ((ing?.marque || ing?.caracteristique) ? ` (${[ing.marque, ing.caracteristique].filter(Boolean).join(' ')})` : '') : '—';
                                                        return (
                                                            <li key={idx}>
                                                                {displayName}: {line.qty} {unit.abbreviation || ing?.unit || ''} — en stock: {stockQty} {unit.abbreviation || ing?.unit || ''}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                        {visibleCostId === menuItem.id && (
                                            <div className="mt-3">
                                                <DishCost dish={menuItem} ingredients={ingredients} costDetails={getDishCostDetails(menuItem)} menuItems={menuItems} recipeOptionMap={recipeOptionMap} />
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 hidden sm:table-cell align-top">
                                        <p className="text-green-600">{getPortionsRemaining(menuItem)}</p>
                                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-green-200">
                                            <div
                                                className="h-full rounded-full bg-green-600"
                                                style={{ width: `${Math.min(100, getPortionsRemaining(menuItem) * 12)}%` }}
                                            />
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 align-top text-green-900 font-semibold">Ar {formatMoney(getDishCostTotal(menuItem))}</td>
                                    <td className="px-4 py-4 align-top text-green-900 font-semibold">Ar {formatMoney(menuItem.labor_charges ?? 0)}</td>
                                    <td className="px-4 py-4 align-top text-green-900 font-bold text-lg">Ar {formatMoney(getDishCostTotal(menuItem) + (menuItem.labor_charges ?? 0))}</td>
                                    <td className="px-4 py-4 align-top text-green-600">Ar {formatMoney(menuItem.price)}</td>
                                    <td className="px-4 py-4 align-top text-green-700 font-semibold">Ar {formatMoney((menuItem.price ?? 0) - (getDishCostTotal(menuItem) + (menuItem.labor_charges ?? 0)))}</td>
                                    <td className="px-4 py-4 align-top">
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                className="rounded-3xl border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-800 hover:border-green-500"
                                                onClick={() => handleEdit(menuItem)}
                                            >
                                                Éditer
                                            </button>
                                            <button
                                                className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-rose-500 bg-red-100 text-rose-500 hover:bg-rose-500/20 text-lg font-light"
                                                onClick={() => onDeleteMenuItem(menuItem.id)}
                                                title="Supprimer ce plat"
                                            >
                                                ×
                                            </button>
                                            <button
                                                className="rounded-3xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:border-slate-500"
                                                onClick={() => setVisibleCostId(visibleCostId === menuItem.id ? null : menuItem.id)}
                                            >
                                                {visibleCostId === menuItem.id ? 'Masquer coût' : 'Voir coût'}
                                            </button>
                                            <button
                                                className="rounded-3xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:border-slate-500"
                                                onClick={() => toggleIngredients(menuItem.id)}
                                            >
                                                {expandedId === menuItem.id ? 'Masquer ingrédients' : 'Voir ingrédients'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
